/**
 * Auth Service for Dashnode AI
 * Integrates with ace_portal_frontend backend API endpoints and OAuth SSO (Google & Microsoft Azure)
 */

export const getRedirectUrl = (action = 'login') => {
  return `${window.location.origin}/?auth_action=${action}`;
};

export const initiateOAuth = async (provider, action = 'login') => {
  try {
    const redirectTo = getRedirectUrl(action);
    const response = await fetch(
      `/api/method/ace_be_app.api.auth.get_oauth2_authorize_url?provider=${provider}&redirect_to=${encodeURIComponent(
        redirectTo
      )}`
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: Failed to reach SSO endpoint`);
    }

    const data = await response.json();
    if (!data?.message) {
      throw new Error('Invalid OAuth authorize response from server');
    }

    localStorage.setItem('auth_provider', provider);
    localStorage.setItem('auth_action', action);

    // Redirect user to the provider's OAuth screen (Google / Microsoft Azure)
    window.location.assign(data.message);
    return { success: true, redirecting: true };
  } catch (error) {
    console.error('SSO Initiation failed:', error);
    return { success: false, error: error.message || 'SSO initiation failed' };
  }
};

export const loginWithEmail = async (email, password) => {
  try {
    const response = await fetch('/api/method/login', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ usr: email, pwd: password }),
    });

    if (response.ok) {
      const data = await response.json();
      const user = {
        email,
        name: data.full_name || email.split('@')[0],
        role: 'Engineer',
        isLoggedIn: true,
      };
      localStorage.setItem('dashnode_user', JSON.stringify(user));
      localStorage.setItem('user_email', email);
      return { success: true, user };
    }

    const errorData = await response.json().catch(() => ({}));
    const errorMsg =
      errorData.message ||
      errorData.exception ||
      'Invalid email or password. Please verify your credentials.';
    return { success: false, error: errorMsg };
  } catch (err) {
    console.error('Login network error:', err);
    return {
      success: false,
      error: 'Network error connecting to Dashnode authentication server.',
    };
  }
};

export const registerWithEmail = async ({ email, password }) => {
  try {
    const payload = {
      email,
      password,
      terms__conditions: 1,
      whatapp_updates: 0,
      meta_data: '',
    };

    const response = await fetch('/api/method/ace_be_app.api.auth.signup', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => ({}));

    if (response.ok && data?.status === 'green') {
      const user = {
        email,
        name: email.split('@')[0],
        role: 'Engineer',
        isLoggedIn: true,
      };
      localStorage.setItem('dashnode_user', JSON.stringify(user));
      localStorage.setItem('user_email', email);
      return { success: true, user, message: 'Account created successfully!' };
    }

    const errorMsg =
      data?.message || data?.data?.message || 'Registration request could not be completed.';
    return { success: false, error: errorMsg };
  } catch (err) {
    console.error('Registration network error:', err);
    return {
      success: false,
      error: 'Network error connecting to Dashnode signup server.',
    };
  }
};

export const logout = async () => {
  try {
    await fetch('/api/method/logout', {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {});
  } finally {
    localStorage.removeItem('dashnode_user');
    localStorage.removeItem('user_email');
    localStorage.removeItem('auth_provider');
    localStorage.removeItem('auth_action');
  }
};

export const getStoredUser = () => {
  try {
    // Check if OAuth callback just landed in URL
    const urlParams = new URLSearchParams(window.location.search);
    const authAction = urlParams.get('auth_action') || localStorage.getItem('auth_action');
    const authProvider = localStorage.getItem('auth_provider');

    if (authProvider && authAction) {
      localStorage.removeItem('auth_provider');
      localStorage.removeItem('auth_action');
    }

    const stored = localStorage.getItem('dashnode_user');
    if (stored) {
      return JSON.parse(stored);
    }
    return null;
  } catch {
    return null;
  }
};
